import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDownIcon, TrashIcon, CalendarIcon, ClockIcon, TableIcon, UsersIcon } from '@/components/icons';
import { formatDateByTimezone, formatTime } from '@/lib/utils';

import type { CartItemResponse } from '@/features/cart/types';
import type { EventBookingCategoryResponse } from '@/features/booking/types/eventBookingCategoryTypes';
import { cartService } from '@/features/cart/services/cartService';
import { getEventImage } from '@/features/cart/utils/cartItemUtils';

interface AvailabilityConflictBannerProps {
  cartItemId: string;
  conflictingItemIds: string[];
  cartItems: CartItemResponse[];
  eventBookingCategories: EventBookingCategoryResponse[];
  onItemRemoved: () => void;
  clubHandle?: string | null;
}

const AvailabilityConflictBanner: React.FC<AvailabilityConflictBannerProps> = ({
  conflictingItemIds,
  cartItems,
  eventBookingCategories,
  onItemRemoved,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRemoving, setIsRemoving] = useState<Record<string, boolean>>({});
  
  // Get timezone from first conflicting item or default
  const firstConflictingItem = cartItems.find(item => conflictingItemIds.includes(item.id));
  const businessTimeZone = firstConflictingItem?.timezone_id || 'Europe/London';

  // Get conflicting cart items
  const conflictingItems = cartItems.filter(item => conflictingItemIds.includes(item.id));

  // Get item details helper
  const getItemDetails = (item: CartItemResponse) => {
    const eventBookingCategory = item.event_booking_category?.id
      ? eventBookingCategories.find(ebc => ebc.id === item.event_booking_category?.id)
      : null;
    
    const eventSchedule = eventBookingCategory?.event_schedule;
    const event = eventSchedule?.event;
    const bookingCategory = eventBookingCategory?.booking_category;

    const eventDate = eventSchedule?.start_time 
      ? formatDateByTimezone(eventSchedule.start_time, businessTimeZone, { format: 'date' })
      : 'Date TBD';
    const eventTime = eventSchedule?.start_time 
      ? formatTime(eventSchedule.start_time, businessTimeZone)
      : 'Time TBD';

    // Get event image
    const eventImage = getEventImage(event || null, bookingCategory || null);

    return {
      eventName: event?.name || 'Unknown Event',
      eventDescription: event?.description || null,
      eventDate,
      eventTime,
      bookingCategoryName: bookingCategory?.name || null,
      tableNumber: item.table_number,
      bookingTime: item.booking_time,
      totalGuests: item.total_guests,
      eventImage,
    };
  };

  // Handle scroll to item
  const handleScrollToItem = (itemId: string) => {
    const element = document.getElementById(`cart-item-${itemId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight the item briefly
      element.style.transition = 'box-shadow 0.3s ease';
      element.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.5)';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 2000);
    }
  };

  // Handle remove item
  const handleRemoveItem = async (itemId: string) => {
    if (isRemoving[itemId]) return;
    
    setIsRemoving(prev => ({ ...prev, [itemId]: true }));
    try {
      await cartService.removeCartItem(itemId);
      onItemRemoved();
    } catch (error) {
      console.error('Failed to remove cart item:', error);
    } finally {
      setIsRemoving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  if (conflictingItems.length === 0) {
    return null;
  }

  const conflictCount = conflictingItems.length;

  return (
    <div className="mb-4">
      {/* Header Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative rounded-2xl overflow-hidden shadow-lg border border-red-500/30"
        style={{
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="relative p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center">
                  <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-red-400 font-semibold text-base">
                  Conflicting Items Detected
                </h3>
              </div>
              <p className="text-gray-300 text-sm ml-7">
                Please remove conflicting items to proceed with checkout
              </p>
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors text-red-400 text-sm font-medium"
            >
              <span>{conflictCount} conflict{conflictCount !== 1 ? 's' : ''}</span>
              <ChevronDownIcon 
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Expanded Conflicts List */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 space-y-3">
              {conflictingItems.map((item) => {
                const details = getItemDetails(item);
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative rounded-2xl overflow-hidden shadow-lg border border-red-500/20"
                    style={{
                      background: 'rgba(0, 0, 0, 0.4)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                    }}
                  >
                    {/* Background Image - Subtle */}
                    {details.eventImage && (
                      <div className="absolute inset-0 opacity-10">
                        <img
                          src={details.eventImage}
                          alt={details.eventName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <div className="relative p-6">
                      <div className="flex items-start justify-between gap-6">
                        {/* Item Details */}
                        <div 
                          className="flex-1 cursor-pointer group"
                          onClick={() => handleScrollToItem(item.id)}
                        >
                          <h4 className="text-white font-bold text-xl mb-2 group-hover:text-red-400 transition-colors">
                            {details.eventName}
                          </h4>
                          {details.eventDescription && (
                            <p className="text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                              {details.eventDescription}
                            </p>
                          )}
                          <div className="space-y-2">
                            <div className="flex items-center gap-4 text-base">
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4">
                                  <CalendarIcon />
                                </div>
                                <span className="font-bold text-gray-300">{details.eventDate}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4">
                                  <ClockIcon />
                                </div>
                                <span className="font-bold text-gray-300">{details.eventTime}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-base">
                              {details.bookingCategoryName && (
                                <span className="text-gray-400">{details.bookingCategoryName}</span>
                              )}
                              {details.tableNumber && (
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4">
                                    <TableIcon />
                                  </div>
                                  <span className="font-bold text-gray-300">Table: {details.tableNumber}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4">
                                  <UsersIcon />
                                </div>
                                <span className="font-bold text-gray-300">
                                  {details.totalGuests} Guest{details.totalGuests !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Remove Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveItem(item.id);
                          }}
                          disabled={isRemoving[item.id]}
                          className="flex items-center gap-2 px-6 py-3 bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors text-red-400 font-medium shadow-lg shadow-red-500/10"
                        >
                          {isRemoving[item.id] ? (
                            <>
                              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Removing...</span>
                            </>
                          ) : (
                            <>
                              <TrashIcon className="w-5 h-5" />
                              <span>Remove</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AvailabilityConflictBanner;

